import { Button, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import PreviewFileDialog from "@entrilia/shared/components/previewFile/PreviewFileDialog";
import type { FileInfo } from "../../api/types/commonApiTypes";
import { useViewOnlyFileUrl } from "../../hooks/useViewOnlyFileUrl";
import type { PdfBlobCheckState } from "../../services/pdfPreview/pdfBlobCheckTypes";
import type { PdfRetrySession, RefreshDocumentUrl } from "../../services/pdfPreview/pdfLoadErrorHandler";
import {
  abortPdfRetrySession,
  handlePdfLoadError as handlePdfPreviewLoadError,
  isPdfRetryActive,
} from "../../services/pdfPreview/pdfLoadErrorHandler";
import { userEmailSelector } from "../../store/state/user/selectors";
import PdfBlobCheckResultDialog from "./PdfBlobCheckResultDialog";
import PreviewStatusDialog, { previewFailedTitle } from "./PreviewStatusDialog";

interface Props {
  url: string;
  fileName: string;
  documentViewSessionId?: string;
  preventDownload?: boolean;
  onClose: () => void;
  refreshUrl?: RefreshDocumentUrl;
}

const InvestorPortalPreviewFileDialog = ({
  url,
  fileName,
  documentViewSessionId,
  preventDownload,
  onClose,
  refreshUrl,
}: Props) => {
  const email = useSelector(userEmailSelector);
  const [checkState, setCheckState] = useState<PdfBlobCheckState>();
  const [overrideInfo, setOverrideInfo] = useState<FileInfo>();
  // Bumped to remount the viewer after a successful retry probe. react-pdf Document
  // is keyed by source, which does not change on retry.
  const [reloadKey, setReloadKey] = useState(0);
  const sessionRef = useRef<PdfRetrySession | undefined>(undefined);
  const effectiveSessionIdRef = useRef<string | undefined>(undefined);

  const sourceUrl = overrideInfo?.downloadUrl ?? url;
  const effectiveSessionId = overrideInfo?.documentViewSessionId ?? documentViewSessionId;
  const { effectiveUrl, decryptFailed, canRetry, isRetrying, retryDecrypt, refreshEffectiveUrl } = useViewOnlyFileUrl(
    sourceUrl,
    refreshUrl
  );

  useEffect(() => {
    effectiveSessionIdRef.current = effectiveSessionId;
  }, [effectiveSessionId]);

  useEffect(
    () => () => {
      const session = sessionRef.current;
      if (session) {
        if (isPdfRetryActive(session)) {
          abortPdfRetrySession(session, effectiveSessionIdRef.current, "document-changed-or-unmounted");
        }
        sessionRef.current = undefined;
      }
      setOverrideInfo(undefined);
      setReloadKey(0);
      setCheckState(undefined);
    },
    // decryptFailed unmounts the viewer, so the in-flight retry cycle must be torn down with it.
    [url, decryptFailed]
  );

  const handlePdfLoadError = async (error: Error, fileSrc: string) => {
    if (!effectiveUrl) {
      return;
    }
    await handlePdfPreviewLoadError({
      fileSrc,
      effectiveUrl,
      error,
      documentViewSessionId: effectiveSessionId,
      session: sessionRef.current,
      refreshUrl: refreshEffectiveUrl,
      callbacks: {
        onSessionCreated: (session) => {
          sessionRef.current = session;
        },
        onRetrying: (attempt, maxAttempts) => {
          setCheckState({ kind: "retrying", attempt, maxAttempts });
        },
        onChecking: () => {
          setCheckState({ kind: "checking" });
        },
        onResolved: setCheckState,
        onRetrySucceeded: (fileInfo) => {
          if (fileInfo) {
            setOverrideInfo(fileInfo);
          }
          setReloadKey((key) => key + 1);
          setCheckState(undefined);
        },
      },
    });
  };

  const handleResultsClose = () => {
    const session = sessionRef.current;
    if (session && isPdfRetryActive(session)) {
      abortPdfRetrySession(session, effectiveSessionId, "results-closed");
    }
    setCheckState(undefined);
  };

  if (decryptFailed) {
    return (
      <PreviewStatusDialog
        title={previewFailedTitle}
        onClose={onClose}
        actions={
          canRetry ? (
            <Button variant="contained" autoFocus loading={isRetrying} onClick={retryDecrypt}>
              Try again
            </Button>
          ) : undefined
        }
      >
        <Typography color="textSecondary">
          Please try again later, or contact your fund manager if the document still won&apos;t open.
        </Typography>
      </PreviewStatusDialog>
    );
  }

  if (!effectiveUrl) {
    return (
      <PreviewStatusDialog title="Preparing this document" onClose={onClose}>
        <InlineLoader />
      </PreviewStatusDialog>
    );
  }

  return (
    <>
      <PreviewFileDialog
        key={reloadKey}
        url={effectiveUrl}
        fileName={fileName}
        documentViewSessionId={effectiveSessionId}
        preventDownload={preventDownload}
        onClose={onClose}
        onPdfLoadError={handlePdfLoadError}
      />
      {checkState && <PdfBlobCheckResultDialog state={checkState} email={email} onClose={handleResultsClose} />}
    </>
  );
};

export default InvestorPortalPreviewFileDialog;
